import React, { useState } from 'react';
import { 
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend 
} from 'recharts';
import { Download, TrendingUp } from 'lucide-react';
import { toast } from 'react-toastify';

// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://store.comparehomeupgrades.com/api';

export function EbayReports({ storeId }) {
  const [startDate, setStartDate]         = useState('');
  const [endDate, setEndDate]             = useState('');
  const [reportType, setReportType]       = useState('sales');
  const [groupBy, setGroupBy]             = useState('day');
  const [loading, setLoading]             = useState(false);
  const [salesData, setSalesData]         = useState(null);
  const [inventoryData, setInventoryData] = useState(null);

  const formatMoney = (value) => (parseFloat(value) || 0).toFixed(2);
  const formatNum   = (value) => parseInt(value) || 0;

  const generateReport = async () => {
    if (reportType !== 'inventory' && (!startDate || !endDate)) {
      toast.error('Please select date range');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

      if (reportType === 'inventory') {
        const response = await fetch(`${API_BASE_URL}/ebay/${storeId}/products`, { headers });
        const data = await response.json();
        if (data.success) {
          setInventoryData(data.products);
          setSalesData(null);
          toast.success('Inventory report generated!');
        } else {
          toast.error('Failed to generate inventory report');
        }
      } else {
        const response = await fetch(
          `${API_BASE_URL}/ebay/${storeId}/reports/sales?start_date=${startDate}&end_date=${endDate}&group_by=${groupBy}`,
          { headers }
        );
        const data = await response.json();
        if (data.success) {
          setSalesData(data);
          setInventoryData(null);
          toast.success(`${reportType === 'sales' ? 'Sales' : 'Profit'} report generated!`);
        } else {
          toast.error('Failed to generate report');
        }
      }
    } catch (error) {
      console.error('Report Generation Error:', error);
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const exportCurrentReport = async () => {
    if (reportType !== 'inventory' && (!startDate || !endDate)) {
      toast.error('Please select date range for export');
      return;
    }
    try {
      const token = localStorage.getItem('token');

      if (reportType === 'inventory') {
        const headers = ['Product', 'SKU', 'Condition', 'Price', 'Cost', 'Stock Value', 'Stock'];
        const rows = inventoryData.map(p => [
          `"${p.title || ''}"`,
          p.sku || '',
          p.condition_type || 'NEW',
          parseFloat(p.price || 0).toFixed(2),
          parseFloat(p.cost_price || 0).toFixed(2),
          (parseFloat(p.cost_price || 0) * (p.inventory_quantity || 0)).toFixed(2),
          p.inventory_quantity || 0
        ]);
        const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `ebay-inventory-${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('CSV exported successfully!');
        return;
      }

      const response = await fetch(
        `${API_BASE_URL}/ebay/${storeId}/reports/export?start_date=${startDate}&end_date=${endDate}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ebay-${reportType}-report-${startDate}-to-${endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV exported successfully!');
    } catch (error) {
      console.error('CSV Export Error:', error);
      toast.error('Export failed');
    }
  };

  const hasData = salesData || inventoryData;

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">eBay Reports & Analytics</h1>

      {/* Control Panel */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Report Configuration</h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
          <div>
            <label className="block text-gray-700 mb-2 font-medium">Report Type</label>
            <select
              value={reportType}
              onChange={(e) => { setReportType(e.target.value); setSalesData(null); setInventoryData(null); }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="sales">Sales Report</option>
              <option value="profit">Profit Analysis</option>
              <option value="inventory">Inventory Report</option>
            </select>
          </div>

          <div>
            <label className="block text-gray-700 mb-2 font-medium">Start Date</label>
            <input
              type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              disabled={reportType === 'inventory'}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          <div>
            <label className="block text-gray-700 mb-2 font-medium">End Date</label>
            <input
              type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
              disabled={reportType === 'inventory'}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          <div>
            <label className="block text-gray-700 mb-2 font-medium">Group By</label>
            <select
              value={groupBy} onChange={(e) => setGroupBy(e.target.value)}
              disabled={reportType === 'inventory'}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="day">Daily</option>
              <option value="week">Weekly</option>
              <option value="month">Monthly</option>
            </select>
          </div>

          <div className="flex items-end gap-2">
            <button
              onClick={generateReport} disabled={loading}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all disabled:opacity-50 font-medium"
            >
              {loading ? 'Generating...' : 'Generate'}
            </button>
            <button
              onClick={exportCurrentReport} disabled={!hasData}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Generate report first, then export"
            >
              <Download size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Sales / Profit Report */}
      {salesData?.summary && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <p className="text-gray-600 mb-2">Total Orders</p>
              <p className="text-3xl font-bold text-blue-600">{formatNum(salesData.summary.total_orders)}</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <p className="text-gray-600 mb-2">Total Revenue</p>
              <p className="text-3xl font-bold text-green-600">£{formatMoney(salesData.summary.total_revenue)}</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <p className="text-gray-600 mb-2">Total Profit</p>
              <p className="text-3xl font-bold text-purple-600">£{formatMoney(salesData.summary.total_profit)}</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <p className="text-gray-600 mb-2">Profit Margin</p>
              <p className="text-3xl font-bold text-orange-600">
                {salesData.summary.total_revenue > 0
                  ? ((salesData.summary.total_profit / salesData.summary.total_revenue) * 100).toFixed(1)
                  : '0.0'}%
              </p>
            </div>
          </div>

          {salesData.data && salesData.data.length > 0 ? (
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                {reportType === 'sales' ? 'Sales Trend Analysis' : 'Profit Analysis Over Time'}
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    {reportType === 'sales' ? 'Revenue Trend' : 'Revenue & Profit Trend'}
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={salesData.data}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis />
                      <Tooltip formatter={(value) => `£${formatMoney(value)}`} />
                      <Legend />
                      <Line type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={2} name="Revenue" dot={{ r: 4 }} />
                      {reportType === 'profit' && (
                        <Line type="monotone" dataKey="profit" stroke="#10b981" strokeWidth={2} name="Profit" dot={{ r: 4 }} />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    {reportType === 'sales' ? 'Order Volume' : 'Cost Breakdown'}
                  </h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={salesData.data}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis />
                      <Tooltip formatter={(value) => reportType === 'sales' ? value : `£${formatMoney(value)}`} />
                      <Legend />
                      {reportType === 'sales' ? (
                        <Bar dataKey="orders" fill="#3b82f6" name="Orders" />
                      ) : (
                        <>
                          <Bar dataKey="revenue" fill="#3b82f6" name="Revenue" />
                          <Bar dataKey="cost" fill="#ef4444" name="Cost" />
                          <Bar dataKey="fees" fill="#f59e0b" name="Fees" />
                        </>
                      )}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg mt-6 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b-2 border-gray-200">
                      <tr>
                        <th className="text-left py-4 px-6 text-gray-600 font-semibold">Period</th>
                        <th className="text-right py-4 px-6 text-gray-600 font-semibold">Orders</th>
                        <th className="text-right py-4 px-6 text-gray-600 font-semibold">Units</th>
                        <th className="text-right py-4 px-6 text-gray-600 font-semibold">Revenue</th>
                        {reportType === 'profit' && (
                          <>
                            <th className="text-right py-4 px-6 text-gray-600 font-semibold">Cost</th>
                            <th className="text-right py-4 px-6 text-gray-600 font-semibold">Fees</th>
                            <th className="text-right py-4 px-6 text-gray-600 font-semibold">Profit</th>
                            <th className="text-right py-4 px-6 text-gray-600 font-semibold">Margin</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {salesData.data.map((row, index) => (
                        <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-4 px-6 font-medium text-gray-800">{row.period}</td>
                          <td className="py-4 px-6 text-right">{formatNum(row.orders)}</td>
                          <td className="py-4 px-6 text-right">{formatNum(row.units_sold)}</td>
                          <td className="py-4 px-6 text-right font-semibold text-blue-600">£{formatMoney(row.revenue)}</td>
                          {reportType === 'profit' && (
                            <>
                              <td className="py-4 px-6 text-right text-red-600">£{formatMoney(row.cost)}</td>
                              <td className="py-4 px-6 text-right text-orange-600">£{formatMoney(row.fees)}</td>
                              <td className="py-4 px-6 text-right font-bold text-green-600">£{formatMoney(row.profit)}</td>
                              <td className="py-4 px-6 text-right font-semibold text-purple-600">
                                {row.revenue > 0 ? ((row.profit / row.revenue) * 100).toFixed(1) : '0.0'}%
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg">
              <div className="flex items-center">
                <TrendingUp className="text-yellow-600 mr-3" size={24} />
                <div>
                  <h3 className="text-yellow-800 font-semibold">No Data Found</h3>
                  <p className="text-yellow-700">No orders found for the selected date range. Try a different range or sync first.</p>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Inventory Report */}
      {reportType === 'inventory' && inventoryData && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-800">Current Inventory Status</h2>
            <div className="flex gap-4 text-sm">
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                In Stock: {inventoryData.filter(p => p.inventory_quantity > 10).length}
              </span>
              <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full font-medium">
                Low Stock: {inventoryData.filter(p => p.inventory_quantity > 0 && p.inventory_quantity <= 10).length}
              </span>
              <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full font-medium">
                Out of Stock: {inventoryData.filter(p => p.inventory_quantity === 0).length}
              </span>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="text-left py-3 px-6 text-gray-600 font-semibold">Product</th>
                    <th className="text-left py-3 px-4 text-gray-600 font-semibold">SKU</th>
                    <th className="text-center py-3 px-4 text-gray-600 font-semibold">Condition</th>
                    <th className="text-right py-3 px-4 text-gray-600 font-semibold">Price</th>
                    <th className="text-right py-3 px-4 text-gray-600 font-semibold">Cost</th>
                    <th className="text-right py-3 px-4 text-gray-600 font-semibold">Stock Value</th>
                    <th className="text-right py-3 px-6 text-gray-600 font-semibold">Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {inventoryData.map((product, index) => {
                    const stockValue = parseFloat(product.cost_price || 0) * (product.inventory_quantity || 0);
                    return (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-6">
                          <p className="font-medium text-gray-800 truncate max-w-xs" title={product.title}>
                            {product.title}
                          </p>
                        </td>
                        <td className="py-3 px-4 text-gray-600">{product.sku || '—'}</td>
                        <td className="py-3 px-4 text-center">
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                            {product.condition_type || 'NEW'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right font-semibold text-blue-600">
                          £{parseFloat(product.price || 0).toFixed(2)}
                        </td>
                        <td className="py-3 px-4 text-right text-gray-600">
                          £{parseFloat(product.cost_price || 0).toFixed(2)}
                        </td>
                        <td className="py-3 px-4 text-right font-semibold text-purple-600">
                          £{stockValue.toFixed(2)}
                        </td>
                        <td className="py-3 px-6 text-right">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            product.inventory_quantity === 0      ? 'bg-red-100 text-red-700' :
                            product.inventory_quantity <= 10      ? 'bg-yellow-100 text-yellow-700' :
                                                                    'bg-green-100 text-green-700'
                          }`}>
                            {product.inventory_quantity}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                  <tr>
                    <td colSpan={5} className="py-3 px-6 font-semibold text-gray-700">Total Stock Value</td>
                    <td className="py-3 px-4 text-right font-bold text-purple-600">
                      £{inventoryData.reduce((sum, p) => sum + (parseFloat(p.cost_price || 0) * (p.inventory_quantity || 0)), 0).toFixed(2)}
                    </td>
                    <td className="py-3 px-6 text-right font-bold text-gray-700">
                      {inventoryData.reduce((sum, p) => sum + (p.inventory_quantity || 0), 0)} units
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EbayReports;